'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import {
  Shield,
  TrendingUp,
  BarChart2,
  Lock,
  ChevronRight,
  ArrowRight,
  Users,
  FileText,
  Globe,
  BookOpen,
  Zap,
  Activity,
  DollarSign,
  PieChart,
  Layers,
  ArrowUpRight,
  AlertTriangle,
  Sliders,
  TrendingDown,
  CheckCircle,
  Building2,
  Briefcase,
  UserCheck,
  Send,
} from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

export default function LandingPage() {
  const [scrollY, setScrollY] = useState(0);
  const heroRef = useRef<HTMLDivElement>(null);
  const [eoiForm, setEoiForm] = useState({
    investor_type: '',
    investable_assets: '',
    name: '',
    phone: '',
    email: '',
    business_name: '',
    message: '',
  });
  const [eoiSubmitting, setEoiSubmitting] = useState(false);
  const [eoiSuccess, setEoiSuccess] = useState(false);
  const [eoiError, setEoiError] = useState('');
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);

  const handleEoiSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!eoiForm.investor_type || !eoiForm.investable_assets || !eoiForm.name || !eoiForm.phone || !eoiForm.email) return;
    setEoiSubmitting(true);
    setEoiError('');
    try {
      const supabase = createClient();
      const { error } = await supabase.from('expression_of_interest').insert({
        full_name: eoiForm.name,
        email: eoiForm.email,
        phone: eoiForm.phone || null,
        investor_type: eoiForm.investor_type,
        investable_assets: eoiForm.investable_assets,
        message: [
          eoiForm.business_name ? `Business: ${eoiForm.business_name}` : '',
          eoiForm.message || '',
        ].filter(Boolean).join('\n') || null,
      });
      if (error) throw error;
      setEoiSuccess(true);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setEoiError(msg);
    } finally {
      setEoiSubmitting(false);
    }
  };

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const insights = [
    {
      tag: 'Macro',
      tagColor: 'rgba(200,129,58,0.12)',
      tagBorder: 'rgba(200,129,58,0.25)',
      tagText: 'var(--primary)',
      title: 'Navigating rate cycles: what history tells us',
      excerpt:
        'Central bank policy pivots have historically created asymmetric opportunities for patient investors. We examine the patterns across five decades of monetary tightening and easing cycles.',
      date: 'April 2026',
      readTime: '8 min read',
      featured: true,
      slug: 'navigating-rate-cycles',
    },
    {
      tag: 'Portfolio',
      tagColor: 'rgba(26,46,107,0.15)',
      tagBorder: 'rgba(26,46,107,0.3)',
      tagText: '#6b8cda',
      title: 'Quality over momentum in uncertain markets',
      excerpt:
        'When macro visibility is low, the premium on business quality rises. We explore what that means for portfolio construction.',
      date: 'March 2026',
      readTime: '6 min read',
      featured: false,
      slug: 'quality-over-momentum',
    },
    {
      tag: 'Strategy',
      tagColor: 'rgba(34,197,94,0.08)',
      tagBorder: 'rgba(34,197,94,0.2)',
      tagText: '#4ade80',
      title: 'The compounding advantage of low turnover',
      excerpt:
        'Transaction costs, taxes, and behavioural drag erode returns silently. A low-turnover approach compounds these savings over time.',
      date: 'February 2026',
      readTime: '5 min read',
      featured: false,
      slug: 'compounding-low-turnover',
    },
    {
      tag: 'Global Macro',
      tagColor: 'rgba(168,85,247,0.08)',
      tagBorder: 'rgba(168,85,247,0.2)',
      tagText: '#c084fc',
      title: 'Dollar dominance and the emerging market opportunity',
      excerpt:
        'A structurally weaker dollar environment opens selective windows in emerging market equities and commodities. We map the opportunity set.',
      date: 'January 2026',
      readTime: '7 min read',
      featured: false,
      slug: 'dollar-dominance-em',
    },
    {
      tag: 'Commodities',
      tagColor: 'rgba(251,146,60,0.1)',
      tagBorder: 'rgba(251,146,60,0.25)',
      tagText: '#fb923c',
      title: 'Gold in a multi-polar world',
      excerpt:
        'Central bank accumulation, de-dollarisation trends, and geopolitical fragmentation are reshaping gold\'s role in institutional portfolios.',
      date: 'December 2024',
      readTime: '6 min read',
      featured: false,
      slug: 'gold-multipolar-world',
    },
    {
      tag: 'Fixed Income',
      tagColor: 'rgba(20,184,166,0.08)',
      tagBorder: 'rgba(20,184,166,0.2)',
      tagText: '#2dd4bf',
      title: 'Duration risk in a higher-for-longer regime',
      excerpt:
        'The repricing of long-duration assets is not yet complete. We assess where the risk-reward sits across the yield curve today.',
      date: 'November 2024',
      readTime: '5 min read',
      featured: false,
      slug: 'duration-risk-higher-for-longer',
    },
  ];

  const marketThemes = [
    { icon: Activity, label: 'Rate Cycle Inflection', status: 'Watching', color: '#fb923c' },
    { icon: DollarSign, label: 'USD Structural Trend', status: 'Bearish', color: '#f87171' },
    { icon: Zap, label: 'Energy Transition', status: 'Constructive', color: '#4ade80' },
    { icon: PieChart, label: 'EM Equities', status: 'Selective', color: '#6b8cda' },
    { icon: Layers, label: 'Credit Spreads', status: 'Cautious', color: '#fb923c' },
    { icon: Globe, label: 'Geopolitical Risk', status: 'Elevated', color: '#f87171' },
  ];

  const featuredInsight = insights[0];
  const gridInsights = insights.slice(1);

  return (
    <div
      className="min-h-screen overflow-x-hidden"
      style={{ backgroundColor: 'var(--background)', color: 'var(--foreground)' }}
    >
      {/* ── NAV ── */}
      <header
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 md:px-12 lg:px-20"
        style={{
          height: '80px',
          overflow: 'visible',
          backgroundColor:
            scrollY > 40
              ? 'rgba(13,26,58,0.92)'
              : 'transparent',
          borderBottom:
            scrollY > 40 ? '1px solid rgba(30,48,104,0.6)' : '1px solid transparent',
          transition: 'background-color 300ms, border-color 300ms',
          isolation: 'isolate',
          willChange: 'auto',
        }}
      >
        {/* Logo */}
        <div className="flex items-center gap-3">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/assets/macro-capital-logo.svg"
            alt="Macro Capital"
            style={{ width: '56px', height: '56px', display: 'block' }}
          />
          <div className="flex flex-col items-start gap-0.5">
            <span
              className="text-base font-bold tracking-[0.08em] uppercase leading-none"
              style={{ color: 'var(--foreground)', letterSpacing: '0.08em' }}
            >
              Macro Capital
            </span>
            <span
              className="text-[10px] font-semibold tracking-[0.18em] uppercase leading-none"
              style={{ color: 'var(--primary)' }}
            >
              Private Wealth Management
            </span>
          </div>
        </div>

        {/* Nav links — desktop */}
        <nav className="hidden md:flex items-center gap-8">
          {[
            { label: 'Philosophy', href: '#philosophy' },
            { label: 'Approach', href: '#approach' },
            { label: 'Risk Management', href: '#risk' },
            { label: 'Insights', href: '#insights' },
            { label: 'Who We Work With', href: '#who-we-work-with' },
            { label: 'Access', href: '#access' },
          ].map((item) => (
            <a
              key={item.label}
              href={item.href}
              className="text-sm font-medium transition-colors"
              style={{ color: 'var(--muted-foreground)' }}
              onMouseEnter={(e) =>
                ((e.target as HTMLElement).style.color = 'var(--foreground)')
              }
              onMouseLeave={(e) =>
                ((e.target as HTMLElement).style.color = 'var(--muted-foreground)')
              }
            >
              {item.label}
            </a>
          ))}
        </nav>
      </header>

      {/* ── HERO ── */}
      <section
        ref={heroRef}
        className="relative flex flex-col items-center justify-center text-center overflow-hidden"
        style={{ minHeight: '100vh', paddingTop: '72px' }}
      >
        {/* Layered background */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(26,46,107,0.55) 0%, transparent 70%)',
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage: `linear-gradient(var(--primary) 1px, transparent 1px), linear-gradient(90deg, var(--primary) 1px, transparent 1px)`,
            backgroundSize: '56px 56px',
            transform: `translateY(${scrollY * 0.15}px)`,
          }}
        />
        {/* Orb top-left */}
        <div
          className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(200,129,58,0.12) 0%, transparent 65%)',
            transform: `translateY(${scrollY * 0.08}px)`,
          }}
        />
        {/* Orb bottom-right */}
        <div
          className="absolute bottom-0 right-0 w-[420px] h-[420px] rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(26,46,107,0.5) 0%, transparent 65%)',
            transform: `translateY(${-scrollY * 0.06}px)`,
          }}
        />

        {/* Gold top rule */}
        <div
          className="absolute top-0 left-0 right-0 h-[2px]"
          style={{
            background:
              'linear-gradient(90deg, transparent, var(--primary) 30%, var(--primary) 70%, transparent)',
          }}
        />

        <div className="relative z-10 max-w-4xl mx-auto px-6">
          {/* Headline */}
          <h1
            className="font-semibold leading-tight mb-6"
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 'clamp(2.6rem, 6vw, 5rem)',
              lineHeight: 1.1,
              color: 'var(--foreground)',
            }}
          >
            Wealth built on{' '}
            <span style={{ color: 'var(--primary)', fontStyle: 'italic' }}>
              conviction.
            </span>
            <br />
            Preserved through{' '}
            <span style={{ color: 'var(--primary)', fontStyle: 'italic' }}>
              discipline.
            </span>
          </h1>

          <p
            className="text-lg leading-relaxed max-w-2xl mx-auto mb-10"
            style={{ color: 'var(--muted-foreground)' }}
          >
            Macro Capital partners with a select group of investors to deploy capital
            with rigour, transparency, and a long-term perspective. We manage wealth
            the way it deserves to be managed.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              href="/login"
              className="flex items-center gap-2 px-7 py-3.5 rounded font-semibold text-sm transition-all"
              style={{
                background: 'linear-gradient(135deg, var(--primary) 0%, var(--gold-dark) 100%)',
                color: '#fff',
                boxShadow: '0 4px 20px rgba(200,129,58,0.35)',
                letterSpacing: '0.02em',
              }}
            >
              Access Your Portfolio
              <ChevronRight size={15} />
            </Link>
            <a
              href="#philosophy"
              className="flex items-center gap-2 px-7 py-3.5 rounded font-semibold text-sm transition-all"
              style={{
                border: '1px solid rgba(200,129,58,0.3)',
                color: 'var(--muted-foreground)',
                backgroundColor: 'rgba(200,129,58,0.05)',
              }}
            >
              Our Philosophy
            </a>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap items-center justify-center gap-6 mt-14">
            {[
              { icon: Shield, label: 'Institutional-grade Security' },
              { icon: Lock, label: 'Invite-only Access' },
              { icon: Globe, label: 'Regulated Framework' },
            ].map((item) => (
              <div
                key={item.label}
                className="flex items-center gap-2 text-xs font-medium"
                style={{ color: 'var(--muted-foreground)' }}
              >
                <item.icon size={13} style={{ color: 'var(--primary)' }} />
                {item.label}
              </div>
            ))}
          </div>
        </div>

        {/* Scroll indicator */}
        <div
          className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40"
          style={{ animation: 'slideUp 2s ease-in-out infinite alternate' }}
        >
          <div className="w-px h-10" style={{ background: 'linear-gradient(180deg, transparent, var(--primary))' }} />
          <span className="text-[10px] tracking-[0.2em] uppercase" style={{ color: 'var(--muted-foreground)' }}>
            Scroll
          </span>
        </div>
      </section>

      {/* ── PHILOSOPHY ── */}
      <section id="philosophy" className="py-28 px-6 md:px-12 lg:px-20 relative">
        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `radial-gradient(circle at 20% 50%, var(--primary) 0%, transparent 50%)`,
          }}
        />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left text */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-px" style={{ backgroundColor: 'var(--primary)' }} />
                <span
                  className="text-xs font-semibold tracking-[0.2em] uppercase"
                  style={{ color: 'var(--primary)' }}
                >
                  Our Philosophy
                </span>
              </div>
              <h2
                className="font-semibold mb-6"
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: 'clamp(2rem, 3.5vw, 3rem)',
                  lineHeight: 1.15,
                  color: 'var(--foreground)',
                }}
              >
                Patient capital.
                <br />
                <span style={{ color: 'var(--primary)', fontStyle: 'italic' }}>
                  Enduring returns.
                </span>
              </h2>
              <p className="text-base leading-relaxed mb-5" style={{ color: 'var(--muted-foreground)' }}>
                We believe that genuine wealth creation demands patience, intellectual
                honesty, and the courage to act against consensus when the evidence
                demands it. Our investment process is built on deep fundamental research
                and a macro-aware framework.
              </p>
              <p className="text-base leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                We do not chase short-term performance. We construct portfolios designed
                to compound capital across market cycles — protecting on the downside,
                participating fully on the upside.
              </p>
            </div>

            {/* Right — bento cards */}
            <div className="grid grid-cols-2 gap-4">
              {[
                {
                  icon: TrendingUp,
                  title: 'Macro-driven',
                  desc: 'Top-down thematic views anchored in global macro research.',
                  span: false,
                },
                {
                  icon: BarChart2,
                  title: 'Risk-aware',
                  desc: 'Downside protection is as important as upside capture.',
                  span: false,
                },
                {
                  icon: Users,
                  title: 'Selective partnerships',
                  desc: 'We work with a limited number of investors to maintain focus and alignment.',
                  span: true,
                },
              ].map((card) => (
                <div
                  key={card.title}
                  className={`p-6 rounded-xl card-hover ${card.span ? 'col-span-2' : ''}`}
                  style={{
                    backgroundColor: 'var(--card)',
                    border: '1px solid var(--border)',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    className="absolute top-0 left-0 right-0 h-px"
                    style={{
                      background:
                        'linear-gradient(90deg, transparent, rgba(200,129,58,0.2), transparent)',
                    }}
                  />
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center mb-4"
                    style={{
                      backgroundColor: 'rgba(200,129,58,0.1)',
                      border: '1px solid rgba(200,129,58,0.2)',
                    }}
                  >
                    <card.icon size={16} style={{ color: 'var(--primary)' }} />
                  </div>
                  <h3
                    className="font-semibold text-sm mb-2"
                    style={{ color: 'var(--foreground)' }}
                  >
                    {card.title}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                    {card.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── APPROACH ── */}
      <section
        id="approach"
        className="py-28 px-6 md:px-12 lg:px-20"
        style={{ backgroundColor: 'var(--secondary)' }}
      >
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-5">
              <div className="w-8 h-px" style={{ backgroundColor: 'var(--primary)' }} />
              <span
                className="text-xs font-semibold tracking-[0.2em] uppercase"
                style={{ color: 'var(--primary)' }}
              >
                Our Approach
              </span>
              <div className="w-8 h-px" style={{ backgroundColor: 'var(--primary)' }} />
            </div>
            <h2
              className="font-semibold"
              style={{
                fontFamily: 'var(--font-serif)',
                fontSize: 'clamp(1.8rem, 3vw, 2.6rem)',
                color: 'var(--foreground)',
              }}
            >
              How we manage capital
            </h2>
          </div>

          {/* Asymmetric feature layout */}
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Large card */}
            <div
              className="lg:col-span-2 p-8 rounded-xl relative overflow-hidden"
              style={{
                backgroundColor: 'var(--card)',
                border: '1px solid var(--border)',
              }}
            >
              <div
                className="absolute top-0 left-0 right-0 h-px"
                style={{
                  background:
                    'linear-gradient(90deg, transparent, rgba(200,129,58,0.3), transparent)',
                }}
              />
              <div
                className="absolute -bottom-16 -right-16 w-48 h-48 rounded-full opacity-10"
                style={{
                  background: 'radial-gradient(circle, var(--primary) 0%, transparent 70%)',
                }}
              />
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center mb-6"
                style={{
                  backgroundColor: 'rgba(200,129,58,0.1)',
                  border: '1px solid rgba(200,129,58,0.25)',
                }}
              >
                <BarChart2 size={18} style={{ color: 'var(--primary)' }} />
              </div>
              <h3
                className="text-xl font-semibold mb-3"
                style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}
              >
                Research-led portfolio construction
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                Every position in our portfolio is the result of rigorous bottom-up
                analysis informed by a top-down macro view. We identify structural
                opportunities — businesses with durable competitive advantages, operating
                in sectors with long runways — and hold them with conviction through
                volatility.
              </p>
            </div>

            {/* Tall narrow card */}
            <div
              className="p-8 rounded-xl relative overflow-hidden flex flex-col justify-between"
              style={{
                backgroundColor: 'rgba(200,129,58,0.06)',
                border: '1px solid rgba(200,129,58,0.2)',
              }}
            >
              <div>
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center mb-6"
                  style={{
                    backgroundColor: 'rgba(200,129,58,0.15)',
                    border: '1px solid rgba(200,129,58,0.3)',
                  }}
                >
                  <Shield size={18} style={{ color: 'var(--primary)' }} />
                </div>
                <h3
                  className="text-xl font-semibold mb-3"
                  style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}
                >
                  Capital preservation first
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                  We size positions to reflect conviction and risk, never speculation.
                  Drawdown management is embedded in our process.
                </p>
              </div>
              <div
                className="mt-8 pt-6"
                style={{ borderTop: '1px solid rgba(200,129,58,0.2)' }}
              >
                <p
                  className="text-xs font-semibold tracking-[0.15em] uppercase"
                  style={{ color: 'var(--primary)' }}
                >
                  Invite-only
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>
                  Access is limited to qualified investors by referral.
                </p>
              </div>
            </div>

            {/* Bottom row */}
            {[
              {
                icon: FileText,
                title: 'Full transparency',
                desc: 'Investors receive detailed reporting on NAV, transactions, and portfolio activity — always up to date.',
              },
              {
                icon: TrendingUp,
                title: 'Long-term compounding',
                desc: 'We measure success in years, not quarters. Our incentives are aligned with yours.',
              },
              {
                icon: Users,
                title: 'Aligned interests',
                desc: 'We invest alongside our clients. Our capital is in the same strategies as yours.',
              },
            ].map((row) => (
              <div
                key={row.title}
                className="p-6 rounded-xl card-hover"
                style={{
                  backgroundColor: 'var(--card)',
                  border: '1px solid var(--border)',
                }}
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center mb-4"
                  style={{
                    backgroundColor: 'rgba(200,129,58,0.08)',
                    border: '1px solid rgba(200,129,58,0.18)',
                  }}
                >
                  <row.icon size={14} style={{ color: 'var(--primary)' }} />
                </div>
                <h4
                  className="font-semibold text-sm mb-2"
                  style={{ color: 'var(--foreground)' }}
                >
                  {row.title}
                </h4>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                  {row.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── RISK MANAGEMENT ── */}
      <section id="risk" className="py-28 px-6 md:px-12 lg:px-20 relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 70% 60% at 0% 50%, rgba(26,46,107,0.18) 0%, transparent 65%)',
          }}
        />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left — text */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-px" style={{ backgroundColor: 'var(--primary)' }} />
                <span
                  className="text-xs font-semibold tracking-[0.2em] uppercase"
                  style={{ color: 'var(--primary)' }}
                >
                  Risk Management
                </span>
              </div>
              <h2
                className="font-semibold mb-6"
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: 'clamp(1.8rem, 3vw, 2.8rem)',
                  lineHeight: 1.15,
                  color: 'var(--foreground)',
                }}
              >
                Protecting capital is not a constraint.
                <br />
                <span style={{ color: 'var(--primary)', fontStyle: 'italic' }}>
                  It is the strategy.
                </span>
              </h2>
              <p className="text-base leading-relaxed mb-5" style={{ color: 'var(--muted-foreground)' }}>
                Most funds treat risk management as a compliance exercise. We treat it as
                the foundation of every investment decision. Before we ask what we can
                gain, we ask what we can lose — and whether that loss is acceptable given
                the full picture.
              </p>
              <p className="text-base leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                Our framework is built around three interlocking disciplines: limiting
                downside exposure at the portfolio level, sizing each position in
                proportion to its risk-adjusted conviction, and actively managing
                drawdowns before they compound into permanent capital impairment.
              </p>

              {/* Stat row */}
              <div className="flex gap-8 mt-10 pt-8" style={{ borderTop: '1px solid var(--border)' }}>
                {[
                  { value: '≤15%', label: 'Max portfolio drawdown target' },
                  { value: '2–5%', label: 'Single position risk limit' },
                  { value: '30d', label: 'Drawdown review cycle' },
                ].map((stat) => (
                  <div key={stat.label}>
                    <p
                      className="text-2xl font-bold mb-1"
                      style={{ fontFamily: 'var(--font-serif)', color: 'var(--primary)' }}
                    >
                      {stat.value}
                    </p>
                    <p className="text-xs leading-snug" style={{ color: 'var(--muted-foreground)' }}>
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — three discipline cards */}
            <div className="flex flex-col gap-5">
              {[
                {
                  icon: AlertTriangle,
                  title: 'Downside Protection',
                  color: '#f87171',
                  colorBg: 'rgba(248,113,113,0.08)',
                  colorBorder: 'rgba(248,113,113,0.2)',
                  points: [
                    'Hard stop-loss rules at the position and portfolio level',
                    'Macro hedges deployed when systemic risk is elevated',
                    'Asymmetric payoff structures prioritised over raw return potential',
                    'Cash held as a strategic asset, not a residual',
                  ],
                },
                {
                  icon: Sliders,
                  title: 'Position Sizing',
                  color: 'var(--primary)',
                  colorBg: 'rgba(200,129,58,0.08)',
                  colorBorder: 'rgba(200,129,58,0.2)',
                  points: [
                    'Kelly-informed sizing adjusted for conviction and liquidity',
                    'No single position exceeds 5% of portfolio at cost',
                    'Concentration risk monitored across sectors and geographies',
                    'Position limits tighten automatically as volatility rises',
                  ],
                },
                {
                  icon: TrendingDown,
                  title: 'Drawdown Management',
                  color: '#6b8cda',
                  colorBg: 'rgba(107,140,218,0.08)',
                  colorBorder: 'rgba(107,140,218,0.2)',
                  points: [
                    'Portfolio-level drawdown reviewed at 5%, 10%, and 15% thresholds',
                    'Exposure reduced systematically as drawdown deepens',
                    'Recovery plans documented before capital is redeployed',
                    'Investor communication triggered at each threshold breach',
                  ],
                },
              ].map((card) => (
                <div
                  key={card.title}
                  className="p-6 rounded-xl relative overflow-hidden"
                  style={{
                    backgroundColor: 'var(--card)',
                    border: '1px solid var(--border)',
                  }}
                >
                  <div
                    className="absolute top-0 left-0 right-0 h-px"
                    style={{
                      background: `linear-gradient(90deg, transparent, ${card.colorBorder}, transparent)`,
                    }}
                  />
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: card.colorBg, border: `1px solid ${card.colorBorder}` }}
                    >
                      <card.icon size={14} style={{ color: card.color }} />
                    </div>
                    <h3 className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>
                      {card.title}
                    </h3>
                  </div>
                  <ul className="space-y-2">
                    {card.points.map((pt) => (
                      <li key={pt} className="flex items-start gap-2.5">
                        <CheckCircle
                          size={12}
                          className="mt-0.5 flex-shrink-0"
                          style={{ color: card.color }}
                        />
                        <span className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                          {pt}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── INSIGHTS ── */}
      <section
        id="insights"
        className="py-28 px-6 md:px-12 lg:px-20 relative"
        style={{ backgroundColor: 'var(--secondary)' }}
      >
        {/* Subtle background orb */}
        <div
          className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full pointer-events-none opacity-[0.04]"
          style={{ background: 'radial-gradient(circle, var(--primary) 0%, transparent 65%)' }}
        />

        <div className="max-w-6xl mx-auto relative z-10">
          {/* Section header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-px" style={{ backgroundColor: 'var(--primary)' }} />
                <span
                  className="text-xs font-semibold tracking-[0.2em] uppercase"
                  style={{ color: 'var(--primary)' }}
                >
                  Insights
                </span>
              </div>
              <h2
                className="font-semibold"
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: 'clamp(1.8rem, 3vw, 2.6rem)',
                  color: 'var(--foreground)',
                }}
              >
                Perspectives on markets
              </h2>
              <p className="text-sm mt-3 max-w-lg" style={{ color: 'var(--muted-foreground)' }}>
                Our views on macro trends, portfolio strategy, and the forces shaping capital markets.
              </p>
            </div>
            <div
              className="hidden md:flex items-center gap-2 text-xs font-medium px-4 py-2 rounded-full"
              style={{
                border: '1px solid rgba(200,129,58,0.2)',
                color: 'var(--muted-foreground)',
                backgroundColor: 'rgba(200,129,58,0.04)',
              }}
            >
              <BookOpen size={12} style={{ color: 'var(--primary)' }} />
              Research &amp; Commentary
            </div>
          </div>

          {/* ── Featured article ── */}
          <div
            className="relative rounded-2xl overflow-hidden mb-8 p-8 md:p-12"
            style={{
              backgroundColor: 'var(--card)',
              border: '1px solid rgba(200,129,58,0.2)',
            }}
          >
            {/* Decorative gradient */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  'radial-gradient(ellipse 60% 80% at 100% 50%, rgba(200,129,58,0.07) 0%, transparent 70%)',
              }}
            />
            <div
              className="absolute top-0 left-0 right-0 h-[2px]"
              style={{
                background:
                  'linear-gradient(90deg, var(--primary) 0%, rgba(200,129,58,0.3) 60%, transparent 100%)',
              }}
            />

            <div className="relative z-10 grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <span
                    className="text-[10px] font-semibold tracking-[0.18em] uppercase px-3 py-1 rounded-full"
                    style={{
                      backgroundColor: 'rgba(200,129,58,0.15)',
                      border: '1px solid rgba(200,129,58,0.3)',
                      color: 'var(--primary)',
                    }}
                  >
                    Featured · {featuredInsight.tag}
                  </span>
                  <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                    {featuredInsight.date}
                  </span>
                </div>
                <h3
                  className="font-semibold mb-4 leading-snug"
                  style={{
                    fontFamily: 'var(--font-serif)',
                    fontSize: 'clamp(1.3rem, 2.5vw, 1.8rem)',
                    color: 'var(--foreground)',
                  }}
                >
                  {featuredInsight.title}
                </h3>
                <p className="text-sm leading-relaxed mb-6" style={{ color: 'var(--muted-foreground)' }}>
                  {featuredInsight.excerpt}
                </p>
                <div className="flex items-center gap-4">
                  <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                    {featuredInsight.readTime}
                  </span>
                  <div className="w-px h-3" style={{ backgroundColor: 'var(--border)' }} />
                  <span
                    className="text-xs font-semibold flex items-center gap-1"
                    style={{ color: 'var(--primary)' }}
                  >
                    <Link href="/insights/navigating-rate-cycles" className="flex items-center gap-1" style={{ color: 'var(--primary)' }}>
                      Read analysis <ArrowUpRight size={11} />
                    </Link>
                  </span>
                </div>
              </div>

              {/* Right side — abstract visual */}
              <div className="hidden md:flex items-center justify-center">
                <div className="relative w-full max-w-xs">
                  {/* Stylised rate-cycle chart bars */}
                  <div className="flex items-end gap-2 h-32 justify-center">
                    {[40, 65, 55, 80, 70, 90, 75, 60, 85, 95, 78, 62, 70, 82, 88].map((h, i) => (
                      <div
                        key={i}
                        className="flex-1 rounded-t-sm"
                        style={{
                          height: `${h}%`,
                          backgroundColor:
                            i >= 12
                              ? 'rgba(200,129,58,0.9)'
                              : i >= 9
                              ? 'rgba(200,129,58,0.7)'
                              : i >= 6
                              ? 'rgba(200,129,58,0.35)'
                              : 'rgba(200,129,58,0.15)',
                          transition: 'height 0.3s ease',
                        }}
                      />
                    ))}
                  </div>
                  <div
                    className="mt-3 h-px w-full"
                    style={{ backgroundColor: 'rgba(200,129,58,0.2)' }}
                  />
                  <div className="flex justify-between mt-2">
                    <span className="text-[9px]" style={{ color: 'var(--muted-foreground)' }}>2019</span>
                    <span className="text-[9px]" style={{ color: 'var(--muted-foreground)' }}>2022</span>
                    <span className="text-[9px]" style={{ color: 'var(--muted-foreground)' }}>2025</span>
                    <span className="text-[9px] font-semibold" style={{ color: 'var(--primary)' }}>Mid-2026</span>
                  </div>
                  <p className="text-[10px] text-center mt-3 tracking-wide" style={{ color: 'var(--muted-foreground)' }}>
                    Rate cycle momentum index
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ── Market Themes strip ── */}
          <div
            className="rounded-xl p-6 mb-8"
            style={{
              backgroundColor: 'var(--card)',
              border: '1px solid var(--border)',
            }}
          >
            <div className="flex items-center gap-3 mb-5">
              <Activity size={13} style={{ color: 'var(--primary)' }} />
              <span
                className="text-xs font-semibold tracking-[0.18em] uppercase"
                style={{ color: 'var(--primary)' }}
              >
                Current Market Themes
              </span>
              <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border)' }} />
              <span className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>
                as of Q2 2026
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {marketThemes.map((theme) => (
                <div
                  key={theme.label}
                  className="flex flex-col gap-2 p-3 rounded-lg"
                  style={{
                    backgroundColor: 'var(--secondary)',
                    border: '1px solid var(--border)',
                  }}
                >
                  <div className="flex items-center justify-between">
                    <theme.icon size={13} style={{ color: theme.color }} />
                    <span
                      className="text-[9px] font-semibold tracking-wide uppercase px-1.5 py-0.5 rounded"
                      style={{
                        backgroundColor: `${theme.color}18`,
                        color: theme.color,
                      }}
                    >
                      {theme.status}
                    </span>
                  </div>
                  <p className="text-xs font-medium leading-tight" style={{ color: 'var(--foreground)' }}>
                    {theme.label}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Article grid ── */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
            {gridInsights.map(({ tag, tagColor, tagBorder, tagText, title, excerpt, date, readTime, slug }) => (
              <Link
                key={title}
                href={`/insights/${slug}`}
                className="p-6 rounded-xl flex flex-col card-hover relative overflow-hidden group"
                style={{
                  backgroundColor: 'var(--card)',
                  border: '1px solid var(--border)',
                  textDecoration: 'none',
                }}
              >
                <div
                  className="absolute top-0 left-0 right-0 h-px"
                  style={{
                    background:
                      'linear-gradient(90deg, transparent, rgba(200,129,58,0.15), transparent)',
                  }}
                />
                <div className="flex items-center justify-between mb-5">
                  <span
                    className="text-[10px] font-semibold tracking-[0.18em] uppercase px-2.5 py-1 rounded-full"
                    style={{
                      backgroundColor: tagColor,
                      border: `1px solid ${tagBorder}`,
                      color: tagText,
                    }}
                  >
                    {tag}
                  </span>
                  <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                    {date}
                  </span>
                </div>
                <h4
                  className="font-semibold text-sm leading-snug mb-3 flex-1"
                  style={{ color: 'var(--foreground)' }}
                >
                  {title}
                </h4>
                <p className="text-xs leading-relaxed mb-4" style={{ color: 'var(--muted-foreground)' }}>
                  {excerpt}
                </p>
                <div className="flex items-center justify-between mt-auto pt-4" style={{ borderTop: '1px solid var(--border)' }}>
                  <span className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>
                    {readTime}
                  </span>
                  <span
                    className="text-[10px] font-semibold flex items-center gap-1"
                    style={{ color: 'var(--primary)' }}
                  >
                    Read analysis <ArrowUpRight size={10} />
                  </span>
                </div>
              </Link>
            ))}
          </div>

        </div>
      </section>

      {/* ── WHO WE WORK WITH ── */}
      <section id="who-we-work-with" className="py-28 px-6 md:px-12 lg:px-20 relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.03]"
          style={{
            backgroundImage: `radial-gradient(circle at 80% 20%, var(--primary) 0%, transparent 50%)`,
          }}
        />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-14">
            <div className="flex items-center justify-center gap-3 mb-5">
              <div className="w-8 h-px" style={{ backgroundColor: 'var(--primary)' }} />
              <span
                className="text-xs font-semibold tracking-[0.2em] uppercase"
                style={{ color: 'var(--primary)' }}
              >
                Who We Work With
              </span>
              <div className="w-8 h-px" style={{ backgroundColor: 'var(--primary)' }} />
            </div>
            <h2
              className="font-semibold mb-4"
              style={{
                fontFamily: 'var(--font-serif)',
                fontSize: 'clamp(1.8rem, 3vw, 2.6rem)',
                color: 'var(--foreground)',
              }}
            >
              Built for investors who think in decades
            </h2>
            <p className="text-sm max-w-xl mx-auto" style={{ color: 'var(--muted-foreground)' }}>
              We are selective by design. Our fund works best for investors who share our
              conviction that patient, disciplined capital allocation outperforms over the long run.
            </p>
          </div>

          {/* Bento grid — varied sizes */}
          <div className="grid lg:grid-cols-3 gap-5 mb-10">
            {/* Large featured card */}
            <div
              className="lg:col-span-2 p-8 rounded-xl relative overflow-hidden"
              style={{
                backgroundColor: 'rgba(200,129,58,0.05)',
                border: '1px solid rgba(200,129,58,0.18)',
              }}
            >
              <div
                className="absolute top-0 left-0 right-0 h-[2px]"
                style={{
                  background: 'linear-gradient(90deg, var(--primary), rgba(200,129,58,0.2), transparent)',
                }}
              />
              <div
                className="absolute -bottom-12 -right-12 w-40 h-40 rounded-full opacity-10"
                style={{ background: 'radial-gradient(circle, var(--primary) 0%, transparent 70%)' }}
              />
              <div className="flex items-start gap-5 relative z-10">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: 'rgba(200,129,58,0.15)',
                    border: '1px solid rgba(200,129,58,0.3)',
                  }}
                >
                  <Users size={20} style={{ color: 'var(--primary)' }} />
                </div>
                <div>
                  <h3
                    className="text-lg font-semibold mb-2"
                    style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}
                  >
                    High Net Worth Individuals
                  </h3>
                  <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--muted-foreground)' }}>
                    Entrepreneurs, senior executives, and professionals who have built significant
                    wealth and are looking for a disciplined, research-driven partner to grow and
                    protect it. Our investors typically have a minimum investable surplus of
                    ₹50 Lakhs and a time horizon of five years or more.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {['₹50L+ investable surplus', '5+ year horizon', 'Growth-oriented', 'Low liquidity need'].map((tag) => (
                      <span
                        key={tag}
                        className="text-[10px] font-medium px-2.5 py-1 rounded-full"
                        style={{
                          backgroundColor: 'rgba(200,129,58,0.1)',
                          border: '1px solid rgba(200,129,58,0.2)',
                          color: 'var(--primary)',
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Tall narrow card */}
            <div
              className="p-7 rounded-xl relative overflow-hidden flex flex-col"
              style={{
                backgroundColor: 'var(--card)',
                border: '1px solid var(--border)',
              }}
            >
              <div
                className="absolute top-0 left-0 right-0 h-px"
                style={{
                  background: 'linear-gradient(90deg, transparent, rgba(107,140,218,0.3), transparent)',
                }}
              />
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center mb-5"
                style={{
                  backgroundColor: 'rgba(107,140,218,0.1)',
                  border: '1px solid rgba(107,140,218,0.2)',
                }}
              >
                <Building2 size={16} style={{ color: '#6b8cda' }} />
              </div>
              <h3
                className="text-base font-semibold mb-3"
                style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}
              >
                Family Offices
              </h3>
              <p className="text-sm leading-relaxed flex-1" style={{ color: 'var(--muted-foreground)' }}>
                Single and multi-family offices seeking a focused, high-conviction
                allocation to complement their broader portfolio. We work as a
                specialist partner, not a generalist manager — bringing depth in
                macro and multi-asset strategy where most family offices lack
                dedicated internal capacity.
              </p>
              <div
                className="mt-5 pt-5 text-xs font-medium"
                style={{ borderTop: '1px solid var(--border)', color: '#6b8cda' }}
              >
                Specialist allocation partner
              </div>
            </div>

            {/* Bottom row — two equal cards */}
            <div
              className="p-7 rounded-xl relative overflow-hidden"
              style={{
                backgroundColor: 'var(--card)',
                border: '1px solid var(--border)',
              }}
            >
              <div
                className="absolute top-0 left-0 right-0 h-px"
                style={{
                  background: 'linear-gradient(90deg, transparent, rgba(74,222,128,0.25), transparent)',
                }}
              />
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center mb-5"
                style={{
                  backgroundColor: 'rgba(74,222,128,0.08)',
                  border: '1px solid rgba(74,222,128,0.2)',
                }}
              >
                <Briefcase size={16} style={{ color: '#4ade80' }} />
              </div>
              <h3
                className="text-base font-semibold mb-3"
                style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}
              >
                Business Owners &amp; Promoters
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                Founders and promoters looking to diversify wealth outside their
                primary business. We help structure a liquid, professionally managed
                portfolio that operates independently of business cycles and provides
                genuine financial resilience.
              </p>
            </div>

            <div
              className="lg:col-span-2 p-7 rounded-xl relative overflow-hidden"
              style={{
                backgroundColor: 'var(--card)',
                border: '1px solid var(--border)',
              }}
            >
              <div
                className="absolute top-0 left-0 right-0 h-px"
                style={{
                  background: 'linear-gradient(90deg, transparent, rgba(200,129,58,0.15), transparent)',
                }}
              />
              <div className="flex items-start gap-5">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: 'rgba(200,129,58,0.08)',
                    border: '1px solid rgba(200,129,58,0.18)',
                  }}
                >
                  <UserCheck size={16} style={{ color: 'var(--primary)' }} />
                </div>
                <div>
                  <h3
                    className="text-base font-semibold mb-3"
                    style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}
                  >
                    The investor profile that fits us best
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
                    {[
                      'Comfortable with illiquidity over short periods',
                      'Focused on real, after-tax, after-fee returns',
                      'Values transparency and direct communication',
                      'Understands that volatility is not the same as risk',
                      'Seeks a long-term partner, not a product provider',
                      'Has a clear financial goal beyond wealth accumulation',
                    ].map((trait) => (
                      <div key={trait} className="flex items-start gap-2">
                        <CheckCircle
                          size={12}
                          className="mt-0.5 flex-shrink-0"
                          style={{ color: 'var(--primary)' }}
                        />
                        <span className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                          {trait}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom note */}
          <div
            className="rounded-xl p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            style={{
              backgroundColor: 'rgba(200,129,58,0.04)',
              border: '1px solid rgba(200,129,58,0.15)',
            }}
          >
            <Lock size={16} className="flex-shrink-0" style={{ color: 'var(--primary)' }} />
            <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
              <span className="font-semibold" style={{ color: 'var(--foreground)' }}>
                Access is by invitation only.
              </span>{' '}
              If you believe you are a good fit, please reach out directly. We review all enquiries and respond when capacity allows.
            </p>
          </div>
        </div>
      </section>

      {/* ── EXPRESSION OF INTEREST ── */}
      <section
        id="access"
        className="py-28 px-6 md:px-12 lg:px-20 relative overflow-hidden"
        style={{ backgroundColor: 'var(--secondary)' }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 60% 70% at 100% 100%, rgba(200,129,58,0.07) 0%, transparent 65%)',
          }}
        />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left — context */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-px" style={{ backgroundColor: 'var(--primary)' }} />
                <span
                  className="text-xs font-semibold tracking-[0.2em] uppercase"
                  style={{ color: 'var(--primary)' }}
                >
                  Expression of Interest
                </span>
              </div>
              <h2
                className="font-semibold mb-6"
                style={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: 'clamp(1.8rem, 3vw, 2.6rem)',
                  lineHeight: 1.15,
                  color: 'var(--foreground)',
                }}
              >
                Begin the
                <br />
                <span style={{ color: 'var(--primary)', fontStyle: 'italic' }}>
                  conversation.
                </span>
              </h2>
              <p className="text-base leading-relaxed mb-8" style={{ color: 'var(--muted-foreground)' }}>
                We accept a limited number of new investors each year. If you believe
                Macro Capital is the right partner for your wealth, share a brief
                expression of interest and we will be in touch when capacity allows.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Lock, text: 'Invite-only access — reviewed individually' },
                  { icon: Shield, text: 'Your information is kept strictly confidential' },
                  { icon: CheckCircle, text: 'We respond to every genuine enquiry' },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-3">
                    <div
                      className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{
                        backgroundColor: 'rgba(200,129,58,0.1)',
                        border: '1px solid rgba(200,129,58,0.2)',
                      }}
                    >
                      <item.icon size={13} style={{ color: 'var(--primary)' }} />
                    </div>
                    <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                      {item.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — form */}
            <div
              className="rounded-2xl p-8 relative overflow-hidden"
              style={{
                backgroundColor: 'var(--card)',
                border: '1px solid rgba(200,129,58,0.2)',
              }}
            >
              <div
                className="absolute top-0 left-0 right-0 h-[2px]"
                style={{
                  background: 'linear-gradient(90deg, var(--primary), rgba(200,129,58,0.3), transparent)',
                }}
              />

              {eoiSuccess ? (
                <div className="flex flex-col items-center justify-center text-center py-10 gap-5">
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center"
                    style={{
                      backgroundColor: 'rgba(74,222,128,0.1)',
                      border: '1px solid rgba(74,222,128,0.25)',
                    }}
                  >
                    <CheckCircle size={26} style={{ color: '#4ade80' }} />
                  </div>
                  <div>
                    <h3
                      className="text-lg font-semibold mb-2"
                      style={{ fontFamily: 'var(--font-serif)', color: 'var(--foreground)' }}
                    >
                      Interest received
                    </h3>
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                      Thank you for reaching out. We review all expressions of interest
                      carefully and will be in touch when capacity allows.
                    </p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleEoiSubmit} className="space-y-6">
                  <div>
                    <label
                      className="block text-xs font-semibold tracking-[0.12em] uppercase mb-2"
                      style={{ color: 'var(--muted-foreground)' }}
                    >
                      I am a <span style={{ color: 'var(--primary)' }}>*</span>
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      {['High Net Worth Individual', 'Family Office', 'Business Owner / Promoter'].map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setEoiForm((f) => ({ ...f, investor_type: type }))}
                          className="px-3 py-2.5 rounded-lg text-xs font-medium text-center transition-all"
                          style={{
                            backgroundColor:
                              eoiForm.investor_type === type
                                ? 'rgba(200,129,58,0.15)'
                                : 'var(--secondary)',
                            border:
                              eoiForm.investor_type === type
                                ? '1px solid rgba(200,129,58,0.5)'
                                : '1px solid var(--border)',
                            color:
                              eoiForm.investor_type === type
                                ? 'var(--primary)'
                                : 'var(--muted-foreground)',
                          }}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Name field — all types */}
                  {eoiForm.investor_type && (
                    <div>
                      <label
                        className="block text-xs font-semibold tracking-[0.12em] uppercase mb-2"
                        style={{ color: 'var(--muted-foreground)' }}
                      >
                        Name <span style={{ color: 'var(--primary)' }}>*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Your full name"
                        value={eoiForm.name}
                        onChange={(e) => setEoiForm((f) => ({ ...f, name: e.target.value }))}
                        className="w-full rounded-lg px-4 py-3 text-sm outline-none transition-all"
                        style={{
                          backgroundColor: 'var(--secondary)',
                          border: '1px solid var(--border)',
                          color: 'var(--foreground)',
                        }}
                        onFocus={(e) => (e.target.style.borderColor = 'rgba(200,129,58,0.5)')}
                        onBlur={(e) => (e.target.style.borderColor = 'var(--border)')}
                      />
                    </div>
                  )}

                  {/* Business Name — Family Office & Business Owner/Promoter only */}
                  {(eoiForm.investor_type === 'Family Office' || eoiForm.investor_type === 'Business Owner / Promoter') && (
                    <div>
                      <label
                        className="block text-xs font-semibold tracking-[0.12em] uppercase mb-2"
                        style={{ color: 'var(--muted-foreground)' }}
                      >
                        Business Name <span style={{ color: 'var(--primary)' }}>*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Your business or family office name"
                        value={eoiForm.business_name}
                        onChange={(e) => setEoiForm((f) => ({ ...f, business_name: e.target.value }))}
                        className="w-full rounded-lg px-4 py-3 text-sm outline-none transition-all"
                        style={{
                          backgroundColor: 'var(--secondary)',
                          border: '1px solid var(--border)',
                          color: 'var(--foreground)',
                        }}
                        onFocus={(e) => (e.target.style.borderColor = 'rgba(200,129,58,0.5)')}
                        onBlur={(e) => (e.target.style.borderColor = 'var(--border)')}
                      />
                    </div>
                  )}

                  {/* Phone field — all types */}
                  {eoiForm.investor_type && (
                    <div>
                      <label
                        className="block text-xs font-semibold tracking-[0.12em] uppercase mb-2"
                        style={{ color: 'var(--muted-foreground)' }}
                      >
                        Phone Number <span style={{ color: 'var(--primary)' }}>*</span>
                      </label>
                      <input
                        type="tel"
                        placeholder="+91 98765 43210"
                        value={eoiForm.phone}
                        onChange={(e) => setEoiForm((f) => ({ ...f, phone: e.target.value }))}
                        className="w-full rounded-lg px-4 py-3 text-sm outline-none transition-all"
                        style={{
                          backgroundColor: 'var(--secondary)',
                          border: '1px solid var(--border)',
                          color: 'var(--foreground)',
                        }}
                        onFocus={(e) => (e.target.style.borderColor = 'rgba(200,129,58,0.5)')}
                        onBlur={(e) => (e.target.style.borderColor = 'var(--border)')}
                      />
                    </div>
                  )}

                  {/* Email field — all types */}
                  {eoiForm.investor_type && (
                    <div>
                      <label
                        className="block text-xs font-semibold tracking-[0.12em] uppercase mb-2"
                        style={{ color: 'var(--muted-foreground)' }}
                      >
                        Email <span style={{ color: 'var(--primary)' }}>*</span>
                      </label>
                      <input
                        type="email"
                        placeholder="you@example.com"
                        value={eoiForm.email}
                        onChange={(e) => setEoiForm((f) => ({ ...f, email: e.target.value }))}
                        className="w-full rounded-lg px-4 py-3 text-sm outline-none transition-all"
                        style={{
                          backgroundColor: 'var(--secondary)',
                          border: '1px solid var(--border)',
                          color: 'var(--foreground)',
                        }}
                        onFocus={(e) => (e.target.style.borderColor = 'rgba(200,129,58,0.5)')}
                        onBlur={(e) => (e.target.style.borderColor = 'var(--border)')}
                      />
                    </div>
                  )}

                  <div>
                    <label
                      className="block text-xs font-semibold tracking-[0.12em] uppercase mb-2"
                      style={{ color: 'var(--muted-foreground)' }}
                    >
                      Investable surplus range <span style={{ color: 'var(--primary)' }}>*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        '₹50L – ₹1Cr',
                        '₹1Cr – ₹5Cr',
                        '₹5Cr – ₹25Cr',
                        '₹25Cr+',
                      ].map((range) => (
                        <button
                          key={range}
                          type="button"
                          onClick={() => setEoiForm((f) => ({ ...f, investable_assets: range }))}
                          className="px-3 py-2.5 rounded-lg text-xs font-medium text-center transition-all"
                          style={{
                            backgroundColor:
                              eoiForm.investable_assets === range
                                ? 'rgba(200,129,58,0.15)'
                                : 'var(--secondary)',
                            border:
                              eoiForm.investable_assets === range
                                ? '1px solid rgba(200,129,58,0.5)'
                                : '1px solid var(--border)',
                            color:
                              eoiForm.investable_assets === range
                                ? 'var(--primary)'
                                : 'var(--muted-foreground)',
                          }}
                        >
                          {range}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label
                      className="block text-xs font-semibold tracking-[0.12em] uppercase mb-2"
                      style={{ color: 'var(--muted-foreground)' }}
                    >
                      Message <span className="font-normal normal-case tracking-normal" style={{ color: 'var(--muted-foreground)' }}>(optional)</span>
                    </label>
                    <textarea
                      rows={4}
                      placeholder="Tell us briefly about your investment goals or how you heard about us…"
                      value={eoiForm.message}
                      onChange={(e) => setEoiForm((f) => ({ ...f, message: e.target.value }))}
                      className="w-full rounded-lg px-4 py-3 text-sm resize-none outline-none transition-all"
                      style={{
                        backgroundColor: 'var(--secondary)',
                        border: '1px solid var(--border)',
                        color: 'var(--foreground)',
                      }}
                      onFocus={(e) => (e.target.style.borderColor = 'rgba(200,129,58,0.5)')}
                      onBlur={(e) => (e.target.style.borderColor = 'var(--border)')}
                    />
                  </div>

                  {eoiError && (
                    <p className="text-xs" style={{ color: '#f87171' }}>
                      {eoiError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={eoiSubmitting || !eoiForm.investor_type || !eoiForm.investable_assets || !eoiForm.name || !eoiForm.phone || !eoiForm.email}
                    className="w-full flex items-center justify-center gap-2 py-3.5 rounded-lg font-semibold text-sm transition-all"
                    style={{
                      background:
                        eoiSubmitting || !eoiForm.investor_type || !eoiForm.investable_assets || !eoiForm.name || !eoiForm.phone || !eoiForm.email
                          ? 'rgba(200,129,58,0.3)'
                          : 'linear-gradient(135deg, var(--primary) 0%, var(--gold-dark) 100%)',
                      color: '#fff',
                      cursor:
                        eoiSubmitting || !eoiForm.investor_type || !eoiForm.investable_assets || !eoiForm.name || !eoiForm.phone || !eoiForm.email
                          ? 'not-allowed' :'pointer',
                      boxShadow:
                        eoiSubmitting || !eoiForm.investor_type || !eoiForm.investable_assets || !eoiForm.name || !eoiForm.phone || !eoiForm.email
                          ? 'none' :'0 4px 16px rgba(200,129,58,0.3)',
                    }}
                  >
                    {eoiSubmitting ? (
                      <>
                        <div
                          className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin"
                        />
                        Submitting…
                      </>
                    ) : (
                      <>
                        <Send size={14} />
                        Submit Expression of Interest
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA BAND ── */}
      <section
        className="py-20 px-6 md:px-12 lg:px-20 relative overflow-hidden"
        style={{ backgroundColor: 'var(--background)' }}
      >
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `linear-gradient(var(--primary) 1px, transparent 1px), linear-gradient(90deg, var(--primary) 1px, transparent 1px)`,
            backgroundSize: '48px 48px',
          }}
        />
        <div
          className="absolute top-0 left-0 right-0 h-px"
          style={{
            background:
              'linear-gradient(90deg, transparent, var(--primary) 30%, var(--primary) 70%, transparent)',
          }}
        />
        <div
          className="absolute bottom-0 left-0 right-0 h-px"
          style={{
            background:
              'linear-gradient(90deg, transparent, rgba(200,129,58,0.3) 30%, rgba(200,129,58,0.3) 70%, transparent)',
          }}
        />
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <h2
            className="font-semibold mb-5"
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 'clamp(1.8rem, 3.5vw, 2.8rem)',
              color: 'var(--foreground)',
            }}
          >
            Already a client?
          </h2>
          <p className="text-base mb-8" style={{ color: 'var(--muted-foreground)' }}>
            Access your portfolio, review your holdings, and track performance — all in
            one secure place.
          </p>
          <Link
            href="/login"
            className="inline-flex items-center gap-2 px-8 py-4 rounded font-semibold text-sm transition-all"
            style={{
              background: 'linear-gradient(135deg, var(--primary) 0%, var(--gold-dark) 100%)',
              color: '#fff',
              boxShadow: '0 4px 24px rgba(200,129,58,0.4)',
              letterSpacing: '0.03em',
            }}
          >
            Sign in to your account
            <ArrowRight size={15} />
          </Link>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer
        className="py-10 px-6 md:px-12 lg:px-20"
        style={{
          backgroundColor: 'var(--background)',
          borderTop: '1px solid var(--border)',
        }}
      >
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Image
            src="/assets/macro-capital-logo.svg"
            alt="Macro Capital"
            width={120}
            height={36}
            style={{ objectFit: 'contain', opacity: 0.7, display: 'block', maxHeight: 'none' }}
          />
          <p className="text-xs text-center" style={{ color: 'var(--muted-foreground)' }}>
            © {new Date().getFullYear()} Macro Capital. All rights reserved. For qualified investors only.
          </p>
          <div className="flex items-center gap-4">
            {['Privacy', 'Terms'].map((item) => (
              <a
                key={item}
                href="#"
                className="text-xs transition-colors hover:underline"
                style={{ color: 'var(--muted-foreground)' }}
                onClick={(e) => {
                  if (item === 'Privacy') { e.preventDefault(); setActiveModal('privacy'); }
                  if (item === 'Terms') { e.preventDefault(); setActiveModal('terms'); }
                }}
              >
                {item}
              </a>
            ))}
          </div>
        </div>
      </footer>

      {/* ── PRIVACY POLICY MODAL ── */}
      {activeModal === 'privacy' && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}
          onClick={() => setActiveModal(null)}
        >
          <div
            className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl p-8"
            style={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setActiveModal(null)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-lg font-semibold transition-colors"
              style={{ color: 'var(--muted-foreground)', backgroundColor: 'var(--muted)' }}
            >
              ×
            </button>
            <h2 className="text-2xl font-bold mb-1" style={{ color: 'var(--foreground)' }}>Privacy Policy</h2>
            <p className="text-xs mb-6" style={{ color: 'var(--muted-foreground)' }}>Last updated: July 2025</p>

            <div className="space-y-5 text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
              <section>
                <h3 className="font-semibold mb-1" style={{ color: 'var(--foreground)' }}>1. Introduction</h3>
                <p>Macro Capital (&quot;we&quot;, &quot;our&quot;, or &quot;us&quot;) is committed to protecting the privacy of our investors and prospective investors. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you visit our platform or submit an expression of interest.</p>
              </section>

              <section>
                <h3 className="font-semibold mb-1" style={{ color: 'var(--foreground)' }}>2. Information We Collect</h3>
                <p>We may collect the following categories of personal information:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li><strong>Identity &amp; Contact:</strong> Full name, email address, phone number, and business or organisation name.</li>
                  <li><strong>Financial Profile:</strong> Investor type (e.g. individual, SMSF, family office) and indicative investable assets range.</li>
                  <li><strong>Communications:</strong> Any messages or notes you include in your enquiry or correspondence with us.</li>
                  <li><strong>Usage Data:</strong> IP address, browser type, pages visited, and time spent on our platform, collected automatically via cookies and analytics tools.</li>
                </ul>
              </section>

              <section>
                <h3 className="font-semibold mb-1" style={{ color: 'var(--foreground)' }}>3. How We Use Your Information</h3>
                <p>We use the information we collect to:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Evaluate and respond to expressions of interest from qualified investors.</li>
                  <li>Communicate with you about our fund, investment opportunities, and updates.</li>
                  <li>Comply with applicable legal, regulatory, and anti-money laundering (AML) obligations.</li>
                  <li>Improve our platform and investor experience.</li>
                  <li>Maintain records as required by Australian financial services law.</li>
                </ul>
              </section>

              <section>
                <h3 className="font-semibold mb-1" style={{ color: 'var(--foreground)' }}>4. Disclosure of Your Information</h3>
                <p>We do not sell or rent your personal information to third parties. We may share your information with:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Our fund administrators, custodians, and professional advisers who are bound by confidentiality obligations.</li>
                  <li>Regulatory bodies (e.g. ASIC, ATO) where required by law.</li>
                  <li>Technology service providers (e.g. cloud hosting, analytics) under strict data processing agreements.</li>
                </ul>
              </section>

              <section>
                <h3 className="font-semibold mb-1" style={{ color: 'var(--foreground)' }}>5. Data Security</h3>
                <p>We implement industry-standard technical and organisational measures to protect your personal information against unauthorised access, alteration, disclosure, or destruction. Access to investor data is restricted to authorised personnel only.</p>
              </section>

              <section>
                <h3 className="font-semibold mb-1" style={{ color: 'var(--foreground)' }}>6. Data Retention</h3>
                <p>We retain your personal information for as long as necessary to fulfil the purposes outlined in this policy, or as required by applicable law. Enquiry data is retained for a minimum of seven (7) years in accordance with financial record-keeping obligations.</p>
              </section>

              <section>
                <h3 className="font-semibold mb-1" style={{ color: 'var(--foreground)' }}>7. Your Rights</h3>
                <p>You have the right to access, correct, or request deletion of your personal information held by us. To exercise these rights, please contact us at <a href="mailto:info.macrocapital@gmail.com" style={{ color: 'var(--primary)' }}>info.macrocapital@gmail.com</a>. We will respond within 30 days.</p>
              </section>

              <section>
                <h3 className="font-semibold mb-1" style={{ color: 'var(--foreground)' }}>8. Cookies</h3>
                <p>Our platform uses cookies and similar tracking technologies to enhance your experience and gather usage analytics. You may disable cookies through your browser settings; however, some features of the platform may not function correctly as a result.</p>
              </section>
            </div>
          </div>
        </div>
      )}

      {/* ── TERMS OF SERVICE MODAL ── */}
      {activeModal === 'terms' && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}
          onClick={() => setActiveModal(null)}
        >
          <div
            className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl p-8"
            style={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setActiveModal(null)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-lg font-semibold transition-colors"
              style={{ color: 'var(--muted-foreground)', backgroundColor: 'var(--muted)' }}
            >
              ×
            </button>
            <h2 className="text-2xl font-bold mb-1" style={{ color: 'var(--foreground)' }}>Terms of Service</h2>
            <p className="text-xs mb-6" style={{ color: 'var(--muted-foreground)' }}>Last updated: July 2025</p>

            <div className="space-y-5 text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
              <section>
                <h3 className="font-semibold mb-1" style={{ color: 'var(--foreground)' }}>1. Acceptance of Terms</h3>
                <p>By accessing or using the Macro Capital platform (&quot;Platform&quot;), you agree to be bound by these Terms of Service (&quot;Terms&quot;). If you do not agree to these Terms, please do not use the Platform. These Terms apply to all visitors, prospective investors, and registered investors.</p>
              </section>

              <section>
                <h3 className="font-semibold mb-1" style={{ color: 'var(--foreground)' }}>2. Nature of the Platform</h3>
                <p>The Platform is operated by Macro Capital and is intended solely for use by <strong>wholesale and sophisticated investors</strong> as defined under the Corporations Act 2001 (Cth). Nothing on this Platform constitutes financial advice, a product disclosure statement, or an offer to the general public. All information is provided for informational purposes only.</p>
              </section>

              <section>
                <h3 className="font-semibold mb-1" style={{ color: 'var(--foreground)' }}>3. Eligibility</h3>
                <p>Access to certain areas of the Platform is restricted to qualified investors who have been verified by Macro Capital. By submitting an expression of interest, you represent and warrant that:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>You meet the criteria of a wholesale or sophisticated investor under applicable law.</li>
                  <li>You are accessing the Platform for your own investment evaluation purposes.</li>
                  <li>All information you provide is accurate, complete, and not misleading.</li>
                </ul>
              </section>

              <section>
                <h3 className="font-semibold mb-1" style={{ color: 'var(--foreground)' }}>4. No Investment Advice</h3>
                <p>The content on this Platform, including fund performance data, NAV history, and market commentary, is provided for informational purposes only and does not constitute investment advice. Past performance is not indicative of future results. You should seek independent financial, legal, and tax advice before making any investment decision.</p>
              </section>

              <section>
                <h3 className="font-semibold mb-1" style={{ color: 'var(--foreground)' }}>5. Investor Portal Access</h3>
                <p>Registered investors granted access to the investor portal agree to:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Keep their login credentials confidential and not share access with any third party.</li>
                  <li>Notify Macro Capital immediately of any unauthorised use of their account.</li>
                  <li>Use the portal solely for reviewing their own investment information.</li>
                  <li>Not attempt to access data belonging to other investors.</li>
                </ul>
              </section>

              <section>
                <h3 className="font-semibold mb-1" style={{ color: 'var(--foreground)' }}>6. Intellectual Property</h3>
                <p>All content on the Platform, including text, graphics, logos, data, and software, is the property of Macro Capital or its licensors and is protected by applicable intellectual property laws. You may not reproduce, distribute, or create derivative works without our prior written consent.</p>
              </section>

              <section>
                <h3 className="font-semibold mb-1" style={{ color: 'var(--foreground)' }}>7. Limitation of Liability</h3>
                <p>To the maximum extent permitted by law, Macro Capital and its officers, directors, and employees shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the Platform or reliance on any information contained herein. Our total liability to you shall not exceed the amount of fees paid by you to Macro Capital in the preceding twelve (12) months.</p>
              </section>

              <section>
                <h3 className="font-semibold mb-1" style={{ color: 'var(--foreground)' }}>8. Confidentiality</h3>
                <p>Any non-public information about Macro Capital&apos;s fund, strategy, portfolio, or investors that you access through the Platform is strictly confidential. You agree not to disclose such information to any third party without our prior written consent.</p>
              </section>

              <section>
                <h3 className="font-semibold mb-1" style={{ color: 'var(--foreground)' }}>9. Modifications to Terms</h3>
                <p>We reserve the right to update these Terms at any time. Changes will be posted on this page with an updated date. Your continued use of the Platform following any changes constitutes your acceptance of the revised Terms.</p>
              </section>

              <section>
                <h3 className="font-semibold mb-1" style={{ color: 'var(--foreground)' }}>10. Governing Law</h3>
                <p>These Terms are governed by the laws of Hyderabad, India. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts of Hyderabad.</p>
              </section>

              <section>
                <h3 className="font-semibold mb-1" style={{ color: 'var(--foreground)' }}>11. Contact</h3>
                <p>For any questions or concerns regarding these Terms, please contact us at <a href="mailto:info.macrocapital@gmail.com" style={{ color: 'var(--primary)' }}>info.macrocapital@gmail.com</a>.</p>
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
